<script lang="ts">
  import { onMount } from "svelte";
  import { getWithJwt, postWithJwt, serverlessRoutes, translate } from "lib";
  import { currentClient, dialogData, errorTrackerStore, showAlert } from "stores";
  import { ButtonComponent } from "ui";
  import MiniSpinner from "../../UI/MiniSpinner.svelte";

  type Provenance = {
    source?: string;
    confidence?: number;
    trainer_verified?: boolean;
    updated_at?: string;
    proposed_value?: unknown;
    requires_review?: boolean;
    last_proposed_value?: unknown;
  };

  type ClarificationItem = {
    question: string;
    field?: string;
    context?: string;
  };

  type AiClientProfileResponse = {
    profile?: Record<string, any>;
    meta?: Record<string, any>;
    last_extracted_at?: string;
  };

  type RenderedField = {
    key: string;
    rawValue: unknown;
    displayValue: string;
    isComplex: boolean;
    isProposal: boolean;
    proposedValue: unknown;
    confidence: number | null;
    trainerVerified: boolean;
    requiresReview: boolean;
  };

  let isLoading = false;
  let errorMessage = "";
  let profile: Record<string, any> | null = null;
  let meta: Record<string, Provenance> = {};
  let lastExtractedAt: string | null = null;
  let clarifications: ClarificationItem[] = [];

  // Inline override input state
  let editingField: string | null = null;
  let overrideValue = "";

  // In-flight action tracking (so we disable buttons + show spinners on the
  // specific row that's pending). Keyed by field key for confirm-field actions
  // and by index for clarification dismissals.
  let pendingFieldAction: string | null = null;
  let pendingClarificationIndex: number | null = null;

  let renderedGroups: Array<{
    title: string;
    fields: RenderedField[];
  }> = [];

  const fieldGroups: Array<{ title: string; prefix: string }> = [
    { title: translate("GENERAL"), prefix: "general_" },
    { title: translate("GOALS"), prefix: "goal_" },
    { title: translate("MEDICAL"), prefix: "medical_" },
    { title: translate("TRAINING"), prefix: "training_" },
    { title: translate("DIET"), prefix: "diet_" },
    { title: translate("LIFESTYLE"), prefix: "lifestyle_" },
    { title: translate("COMMUNICATION"), prefix: "communication_" },
    { title: translate("ENGAGEMENT"), prefix: "engagement_" },
    { title: translate("BUSINESS"), prefix: "business_" },
  ];

  const formatLabel = (key: string): string => {
    const stripped = key.replace(
      /^(general|goal|medical|training|diet|lifestyle|communication|engagement|business)_/,
      ""
    );
    return stripped
      .split("_")
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(" ");
  };

  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    if (Array.isArray(value)) {
      if (value.length === 0) return "—";
      if (typeof value[0] === "object") {
        return JSON.stringify(value, null, 2);
      }
      return value.join(", ");
    }
    if (typeof value === "object") {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const isComplexValue = (value: unknown): boolean => {
    if (value === null || value === undefined) return false;
    if (Array.isArray(value)) {
      return value.length > 0 && typeof value[0] === "object";
    }
    return typeof value === "object";
  };

  const formatTimestamp = (iso: string | null | undefined): string => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString();
    } catch {
      return iso;
    }
  };

  const isEmptyValue = (value: unknown): boolean => {
    if (value === null || value === undefined || value === "") return true;
    if (Array.isArray(value) && value.length === 0) return true;
    return false;
  };

  const groupedFields = (
    data: Record<string, any>,
    prefix: string
  ): Array<{ key: string; value: unknown }> => {
    return Object.keys(data)
      .filter((key) => key.startsWith(prefix))
      .sort()
      .map((key) => ({ key, value: data[key] }));
  };

  // Decide what to render for a given field. Three relevant render states:
  //   - trainer_verified  → locked-in green check, no proposal treatment
  //   - requires_review   → proposal card (proposed_value or current value)
  //   - has value         → normal value + confidence
  //   - empty + nothing   → em-dash, no confidence
  const enrichField = (
    key: string,
    value: unknown,
    fieldMeta: Provenance | undefined
  ): RenderedField => {
    const m = fieldMeta || {};
    const trainerVerified = !!m.trainer_verified;
    const requiresReview = !!m.requires_review && !trainerVerified;

    const empty = isEmptyValue(value);
    const proposal = m.proposed_value;
    const hasProposal = !isEmptyValue(proposal);

    // Treat as proposal card when the backend flagged requires_review,
    // OR when the field is empty but a proposal exists (legacy behavior).
    const isProposal = requiresReview || (empty && hasProposal);

    // What value to display: prefer proposed_value when in proposal state,
    // otherwise the current column value.
    const displayed: unknown = isProposal && hasProposal ? proposal : value;
    const showConfidence = !empty || hasProposal || requiresReview;

    return {
      key,
      rawValue: value,
      displayValue: formatValue(displayed),
      isComplex: isComplexValue(displayed),
      isProposal,
      proposedValue: hasProposal ? proposal : displayed,
      confidence:
        showConfidence && typeof m.confidence === "number" ? m.confidence : null,
      trainerVerified,
      requiresReview,
    };
  };

  // Sort within a group: fields needing review first, then low confidence,
  // then everything else by key. Trainer-verified fields are deprioritized
  // since they don't need attention.
  const sortByUrgency = (fields: RenderedField[]): RenderedField[] => {
    const urgencyScore = (f: RenderedField): number => {
      if (f.trainerVerified) return 100; // bottom
      if (f.requiresReview) return 0; // top
      if (f.isProposal) return 1;
      if (typeof f.confidence === "number" && f.confidence < 0.6) return 2;
      if (typeof f.confidence === "number" && f.confidence < 0.85) return 5;
      return 10;
    };
    return [...fields].sort((a, b) => {
      const sa = urgencyScore(a);
      const sb = urgencyScore(b);
      if (sa !== sb) return sa - sb;
      return a.key.localeCompare(b.key);
    });
  };

  // Color class for the confidence percentage badge.
  const confidenceColorClass = (confidence: number | null): string => {
    if (confidence == null) return "opacity-60";
    if (confidence < 0.6) return "text-red-500";
    if (confidence < 0.85) return "text-amber-500";
    return "text-emerald-500";
  };

  $: renderedGroups = profile
    ? fieldGroups
        .map((group) => ({
          title: group.title,
          fields: sortByUrgency(
            groupedFields(profile as Record<string, any>, group.prefix).map(
              (f) => enrichField(f.key, f.value, meta[f.key])
            )
          ),
        }))
        .filter((group) => group.fields.length > 0)
    : [];

  // Pull clarifications out of meta. The key is a synthetic top-level entry
  // shaped like { items: ClarificationItem[] }, not a per-field provenance.
  $: {
    const raw = (meta as any)?._clarifications_needed;
    const items = Array.isArray(raw?.items) ? raw.items : [];
    clarifications = items.filter(
      (it: any): it is ClarificationItem =>
        it && typeof it === "object" && typeof it.question === "string"
    );
  }

  const applyResponsePayload = (response: any): void => {
    const payload: AiClientProfileResponse =
      response?.data ?? response ?? {};
    profile = payload.profile ?? profile;
    meta = (payload.meta ?? meta) as Record<string, Provenance>;
    if (payload.last_extracted_at !== undefined) {
      lastExtractedAt = payload.last_extracted_at ?? lastExtractedAt;
    }
  };

  const fetchAiProfile = async (force = false): Promise<void> => {
    isLoading = true;
    errorMessage = "";
    profile = null;
    meta = {};
    lastExtractedAt = null;

    try {
      const url = `${serverlessRoutes.USER}/${$currentClient.id}/ai-profile${
        force ? "?force=true" : ""
      }`;
      const response = await getWithJwt(url);

      const payload: AiClientProfileResponse = response?.data ?? response ?? {};
      profile = payload.profile ?? null;
      meta = (payload.meta ?? {}) as Record<string, Provenance>;
      lastExtractedAt = payload.last_extracted_at ?? null;

      if (!profile) {
        errorMessage = translate("AI_CLIENT_PROFILE_EMPTY");
      }
    } catch (error) {
      errorMessage = translate("AI_CLIENT_PROFILE_ERROR");
      $showAlert.color = "red-500";
      $showAlert.message = translate("AI_CLIENT_PROFILE_ERROR");
      errorTrackerStore.addError("AiClientProfile:fetchAiProfile", { error });
    } finally {
      isLoading = false;
    }
  };

  // POST .../ai-profile/confirm-field — confirm, reject, override, or clear.
  const submitFieldAction = async (
    field: string,
    action: "confirm" | "reject" | "clear",
    value?: unknown
  ): Promise<void> => {
    if (!$currentClient?.id) return;
    pendingFieldAction = field;
    try {
      const body: Record<string, unknown> = { field, action };
      if (value !== undefined) body.value = value;
      const url = `${serverlessRoutes.USER}/${$currentClient.id}/ai-profile/confirm-field`;
      const response = await postWithJwt(url, body);
      applyResponsePayload(response);
      // Close any open override editor for this field on success.
      if (editingField === field) {
        editingField = null;
        overrideValue = "";
      }
    } catch (error) {
      $showAlert.color = "red-500";
      $showAlert.message = translate("AI_CLIENT_PROFILE_ACTION_FAILED");
      errorTrackerStore.addError("AiClientProfile:submitFieldAction", {
        error,
        field,
        action,
      });
    } finally {
      pendingFieldAction = null;
    }
  };

  const onConfirmField = (field: RenderedField): void => {
    // No explicit value → backend uses the proposed_value from meta.
    void submitFieldAction(field.key, "confirm");
  };

  const onRejectField = (field: RenderedField): void => {
    void submitFieldAction(field.key, "reject");
  };

  const onResetField = (field: RenderedField): void => {
    void submitFieldAction(field.key, "clear");
  };

  const onStartOverride = (field: RenderedField): void => {
    editingField = field.key;
    overrideValue =
      field.proposedValue == null
        ? ""
        : typeof field.proposedValue === "object"
        ? JSON.stringify(field.proposedValue)
        : String(field.proposedValue);
  };

  const onCancelOverride = (): void => {
    editingField = null;
    overrideValue = "";
  };

  const onSubmitOverride = (field: RenderedField): void => {
    const trimmed = overrideValue.trim();
    if (!trimmed) return;
    void submitFieldAction(field.key, "confirm", trimmed);
  };

  // POST .../ai-profile/dismiss-clarification — drop one item from the queue.
  const onDismissClarification = async (index: number): Promise<void> => {
    if (!$currentClient?.id) return;
    pendingClarificationIndex = index;
    try {
      const url = `${serverlessRoutes.USER}/${$currentClient.id}/ai-profile/dismiss-clarification`;
      const response = await postWithJwt(url, { index });
      applyResponsePayload(response);
    } catch (error) {
      $showAlert.color = "red-500";
      $showAlert.message = translate("AI_CLIENT_PROFILE_ACTION_FAILED");
      errorTrackerStore.addError("AiClientProfile:onDismissClarification", {
        error,
        index,
      });
    } finally {
      pendingClarificationIndex = null;
    }
  };

  const onClose = (): void => {
    dialogData.update((current) => ({
      ...current,
      type: "",
      data: {},
    }));
  };

  onMount(() => {
    fetchAiProfile();
  });
</script>

<div class="p-4 flex flex-col gap-4">
  <div class="font-medium text-center text-xxl">
    {translate("AI_CLIENT_PROFILE")}
  </div>

  {#if $currentClient?.name}
    <div class="text-center text-sm opacity-70">
      {$currentClient.name}
    </div>
  {/if}

  {#if isLoading}
    <div class="w-full flex items-center justify-center py-12 gap-3">
      <MiniSpinner />
      <div class="text-sm">{translate("AI_CLIENT_PROFILE_LOADING")}</div>
    </div>
  {:else if errorMessage}
    <div class="text-center text-sm text-red-500 py-8">
      {errorMessage}
    </div>
    <div class="flex justify-center">
      <ButtonComponent on:click={() => fetchAiProfile(true)}>
        {translate("RETRY")}
      </ButtonComponent>
    </div>
  {:else if profile}
    {#if lastExtractedAt}
      <div class="text-xs text-center opacity-60">
        {translate("AI_CLIENT_PROFILE_LAST_EXTRACTED")}: {formatTimestamp(lastExtractedAt)}
      </div>
    {/if}

    {#if clarifications.length > 0}
      <div
        class="rounded border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 overflow-hidden"
      >
        <div
          class="px-3 py-2 text-sm font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2"
        >
          <span aria-hidden="true">⚠</span>
          <span>
            {translate("AI_CLIENT_PROFILE_CLARIFICATIONS_TITLE")} ({clarifications.length})
          </span>
        </div>
        <ul class="divide-y divide-amber-200 dark:divide-amber-800">
          {#each clarifications as item, index (index)}
            <li class="px-3 py-2 flex flex-col gap-1">
              <div class="text-sm">
                <span class="opacity-60 mr-1">{index + 1}.</span>{item.question}
              </div>
              {#if item.field}
                <div class="text-xs opacity-70">
                  <span class="font-semibold">
                    {translate("AI_CLIENT_PROFILE_FIELD_LABEL")}:
                  </span>
                  {item.field}
                </div>
              {/if}
              {#if item.context}
                <div class="text-xs opacity-70">
                  <span class="font-semibold">
                    {translate("AI_CLIENT_PROFILE_CONTEXT_LABEL")}:
                  </span>
                  {item.context}
                </div>
              {/if}
              <div class="pt-1">
                <ButtonComponent
                  disabled={pendingClarificationIndex === index}
                  isLoading={pendingClarificationIndex === index}
                  on:click={() => onDismissClarification(index)}
                >
                  {translate("AI_CLIENT_PROFILE_MARK_RESOLVED")}
                </ButtonComponent>
              </div>
            </li>
          {/each}
        </ul>
      </div>
    {/if}

    <div class="flex flex-col gap-4 max-h-[60vh] overflow-y-auto pr-2">
      {#each renderedGroups as group}
        <section class="flex flex-col gap-2">
          <h6 class="font-semibold text-sm uppercase tracking-wide opacity-80">
            {group.title}
          </h6>
          <div class="flex flex-col gap-2">
            {#each group.fields as field (field.key)}
              <div
                class="flex flex-col gap-1 p-2 rounded border border-slate-200 dark:border-zinc-700"
                class:border-amber-300={field.isProposal}
                class:dark:border-amber-700={field.isProposal}
                class:border-emerald-300={field.trainerVerified}
                class:dark:border-emerald-700={field.trainerVerified}
              >
                <div class="flex justify-between items-start gap-3">
                  <div class="text-xs opacity-70 flex items-center gap-1">
                    <span>{formatLabel(field.key)}</span>
                    {#if field.trainerVerified}
                      <span
                        class="text-emerald-500"
                        title={translate("AI_CLIENT_PROFILE_TRAINER_VERIFIED")}
                      >
                        ✓
                      </span>
                      <button
                        type="button"
                        class="text-[10px] underline opacity-60 hover:opacity-100 disabled:opacity-30"
                        disabled={pendingFieldAction === field.key}
                        on:click={() => onResetField(field)}
                      >
                        {translate("AI_CLIENT_PROFILE_RESET")}
                      </button>
                    {/if}
                  </div>
                  {#if field.confidence != null}
                    <div
                      class="text-xs shrink-0 {confidenceColorClass(field.confidence)}"
                    >
                      {Math.round(field.confidence * 100)}%
                    </div>
                  {/if}
                </div>

                {#if field.isProposal}
                  <div
                    class="text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 self-start"
                  >
                    {translate("AI_CLIENT_PROFILE_PROPOSED")}
                  </div>
                {/if}

                {#if field.isComplex}
                  <pre
                    class="text-xs whitespace-pre-wrap break-words font-mono opacity-90">{field.displayValue}</pre>
                {:else}
                  <div class="text-sm break-words">
                    {field.displayValue}
                  </div>
                {/if}

                {#if field.isProposal}
                  {#if editingField === field.key}
                    <div class="flex flex-col gap-2 pt-1">
                      <input
                        type="text"
                        class="text-sm px-2 py-1 rounded border border-slate-300 dark:border-zinc-600 bg-transparent"
                        placeholder={translate(
                          "AI_CLIENT_PROFILE_OVERRIDE_PLACEHOLDER"
                        )}
                        bind:value={overrideValue}
                        disabled={pendingFieldAction === field.key}
                      />
                      <div class="flex gap-2">
                        <ButtonComponent
                          disabled={pendingFieldAction === field.key ||
                            !overrideValue.trim()}
                          isLoading={pendingFieldAction === field.key}
                          on:click={() => onSubmitOverride(field)}
                        >
                          {translate("AI_CLIENT_PROFILE_SAVE")}
                        </ButtonComponent>
                        <ButtonComponent
                          disabled={pendingFieldAction === field.key}
                          on:click={onCancelOverride}
                        >
                          {translate("AI_CLIENT_PROFILE_CANCEL")}
                        </ButtonComponent>
                      </div>
                    </div>
                  {:else}
                    <div class="flex flex-wrap gap-2 pt-1">
                      <ButtonComponent
                        disabled={pendingFieldAction === field.key}
                        isLoading={pendingFieldAction === field.key}
                        on:click={() => onConfirmField(field)}
                      >
                        {translate("AI_CLIENT_PROFILE_CONFIRM")}
                      </ButtonComponent>
                      <ButtonComponent
                        disabled={pendingFieldAction === field.key}
                        on:click={() => onStartOverride(field)}
                      >
                        {translate("AI_CLIENT_PROFILE_OVERRIDE")}
                      </ButtonComponent>
                      <ButtonComponent
                        disabled={pendingFieldAction === field.key}
                        on:click={() => onRejectField(field)}
                      >
                        {translate("AI_CLIENT_PROFILE_REJECT")}
                      </ButtonComponent>
                    </div>
                  {/if}
                {/if}
              </div>
            {/each}
          </div>
        </section>
      {/each}
    </div>

    <div class="flex justify-center gap-2 pt-2">
      <ButtonComponent on:click={() => fetchAiProfile(true)}>
        {translate("AI_CLIENT_PROFILE_REFRESH")}
      </ButtonComponent>
      <ButtonComponent on:click={onClose}>
        {translate("CLOSE")}
      </ButtonComponent>
    </div>
  {/if}
</div>
